import React, { useEffect, useState } from "react";

//UI Material
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
} from "@mui/material";

//Helpers
import {
  typographyForCustomizedFontSize,
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";
import { fetchWithTokenJSONPost } from "../../Helpers/network";

//Material Icons
import { ArrowForwardIos } from "@mui/icons-material";

//Function to generate tabs
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-orders-tabpanel-${index}`}
      aria-labelledby={`client-orders-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{typographyForText(children)}</Box>}
    </div>
  );
}

export default function ClientProducts({ products = [] }) {
  // ==========> Component States <==========
  //State for loading products from db
  const [isLoading, setIsLoading] = useState(true);
  //State for error in request from db
  const [isError, setIsError] = useState(false);
  //State for client products objects
  const [clientProducts, setClientProducts] = useState([]);
  // State for tab value
  const [tabValue, setTabValue] = useState(0);
  //State for select page
  const [page, setPage] = useState(0);
  //State for manage rows per page
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ==========> Component Functions <==========
  //Function to get client data from db
  const getClientProducts = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/orders/get-orders-by-ids`,
        {
          orderIds: products,
        }
      );
      const jsonResponse = await requestResponse.json();
      setClientProducts(jsonResponse.data);
    } catch (error) {
      setIsError(true);
    }
    setIsLoading(false);
  };

  //Function to set tab value
  const handleChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  //Function for giving an index to each tab
  function a11yProps(index) {
    return {
      id: `client-orders-tab-${index}`,
      "aria-controls": `client-orders-tabpanel-${index}`,
    };
  }

  //Function to display products rows
  const generateProductsRows = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center">
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (isError) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center">
            No se pudo obtener la lista de órdenes registradas
          </TableCell>
        </TableRow>
      );
    }

    if (clientProducts && clientProducts.length > 0) {
      return clientProducts.map((product) => (
        <TableRow key={`product-#${product.orderNumber}`}>
          <TableCell align="center">{product.orderNumber}</TableCell>
          <TableCell align="center">{product.numberOfProducts}</TableCell>
          <TableCell align="center">{product.productName}</TableCell>
          <TableCell align="center">
            <Button
              variant="contained"
              size="small"
              color="secondary"
              sx={{ color: "white" }}
              onClick={() => {
                window.location.href = `/order?orderId=${product.orderNumber}`;
              }}
              endIcon={<ArrowForwardIos />}
            >
              Ver orden
            </Button>
          </TableCell>
        </TableRow>
      ));
    }

    return (
      <TableRow>
        <TableCell colSpan={4} align="center">
          Sin órdenes registradas
        </TableCell>
      </TableRow>
    );
  };

  //Function to get total payed taxes amount
  const getAmounts = () => {
    let totals = {
      buyedProducts: 0,
      totalPretax: 0,
      totalTaxes: 0,
      totalAmount: 0,
      lastDateOfOrder: "Sin información",
    };
    for (const product of clientProducts) {
      totals.buyedProducts += Number(product.numberOfProducts);
      totals.totalPretax += Number(product.billing.pretax || 0);
      totals.totalTaxes += Number(product.billing.taxes || 0);
      totals.totalAmount += Number(product.billing.amount || 0);

      if (product.finishedAt && product.finishedAt > totals.lastDateOfOrder) {
        totals.lastDateOfOrder =
          new Date(product.finishedAt).toLocaleDateString() ||
          "Sin información";
      }
    }
    return totals;
  };

  //Function to manage page selection
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };
  //Function to control rows per page options
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ==========> Component UseEffects <==========
  //The useEffect checks for token validation
  useEffect(() => {
    getClientProducts();
  }, []);

  // ==========> Component <==========
  return (
    <Card raised sx={{ height: "100%" }}>
      <Grid container sx={{ padding: "20px" }} spacing={1}>
        <Grid item xs={12}>
          {typographyForSubtitles(
            "Historial de órdenes del cliente",
            "secondary"
          )}
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center">
            {typographyForSubtitles(products.length)}
            {typographyForText("órdenes registradas")}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Divider flexItem />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Tabs
              centered
              indicatorColor="secondary"
              textColor="secondary"
              value={tabValue}
              onChange={handleChange}
              aria-label="product-tabs"
            >
              <Tab
                label="Lista de registros"
                sx={{ fontSize: "11px" }}
                {...a11yProps(0)}
              />
              <Tab
                label="Reportes"
                sx={{ fontSize: "11px" }}
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12} align="center">
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table aria-label="orders-of-client-table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        {typographyForCustomizedFontSize(
                          "No. Orden",
                          "11px",
                          null,
                          "bold"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {typographyForCustomizedFontSize(
                          "Cantidad",
                          "11px",
                          null,
                          "bold"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {typographyForCustomizedFontSize(
                          "Descripción de producto",
                          "11px",
                          null,
                          "bold"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {typographyForCustomizedFontSize(
                          "Acciones",
                          "11px",
                          null,
                          "bold"
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{generateProductsRows()}</TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={clientProducts.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage="Filas"
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <Grid
                container
                justifyContent="flex-start"
                alignItems="flex-start"
                textAlign="left"
              >
                <Grid item xs={12}>
                  {typographyForText("Productos adquiridos", "bold")}
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {typographyForText("Cantidad de productos solicitados:")}
                    {typographyForText(`${getAmounts().buyedProducts}`)}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {typographyForText("Fecha de última órden entregada:")}
                    {typographyForText(`${getAmounts().lastDateOfOrder}`)}
                  </Stack>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: 1 }}>
                  {typographyForText("Facturación", "bold")}
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {typographyForText("Monto sin impuestos pagado:")}
                    {typographyForText(`$${getAmounts().totalPretax}`)}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {typographyForText("Monto IVA pagado:")}
                    {typographyForText(`$${getAmounts().totalTaxes}`)}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {typographyForText("Monto total pagado:")}
                    {typographyForText(`$${getAmounts().totalAmount}`)}
                  </Stack>
                </Grid>
              </Grid>
            </TabPanel>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}
